import { createBrowserRouter } from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth.js';
import { AppShell } from './AppShell.js';
import { AllowlistPage } from './pages/AllowlistPage.js';
import { CampaignPage } from './pages/CampaignPage.js';
import { GuidePage } from './pages/GuidePage.js';
import { HomePage } from './pages/HomePage.js';
import { LicencePage } from './pages/LicencePage.js';
import { LoginPage } from './pages/LoginPage.js';
import { RulesPage } from './pages/RulesPage.js';
import { MapSetupPage } from './map/MapSetupPage.js';
import { NewCampaignPage } from './pages/NewCampaignPage.js';
import { NewCharacterPage } from './characters/NewCharacterPage.js';
import { RequireReady, RequireSeat } from './pages/EnterCampaign.js';
import { CharacterSheetPage } from './characters/CharacterSheetPage.js';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        index: true,
        element: (
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        ),
      },
      {
        path: 'allowlist',
        element: (
          <RequireAuth>
            <AllowlistPage />
          </RequireAuth>
        ),
      },
      {
        // Before the `:campaignId` route, or "new" is read as an id.
        path: 'campaigns/new',
        element: (
          <RequireAuth>
            <NewCampaignPage />
          </RequireAuth>
        ),
      },
      {
        // ⚠ **The resumable half of the setup wizard.** Same component as
        // `campaigns/new` — it reads `campaignId` from the URL and hydrates every field from the server,
        // which is what makes a reload mid-setup pick up where it left off
        // instead of losing progress that only ever lived in local state.
        path: 'campaigns/:campaignId/setup',
        element: (
          <RequireAuth>
            <NewCampaignPage />
          </RequireAuth>
        ),
      },
      {
        // ⚠ Before the bare `:campaignId` route is fine either way — this has a
        // second segment — but the map setup is its own page precisely because
        // it is a waterfall and not a sidebar.
        path: 'campaigns/:campaignId/map',
        element: (
          <RequireAuth>
            <MapSetupPage />
          </RequireAuth>
        ),
      },
      {
        path: 'campaigns/:campaignId',
        element: (
          <RequireAuth>
            {/* ⚠ **`RequireReady` sits outside `RequireSeat`** — a table
                cannot open without a map, a scale and a generated world.
                A seated Owner — seated the moment they
                create the table with a character, before any of the three
                exist — would otherwise sail straight past `RequireSeat`
                (which only ever asked "is there a seat", never "is it
                ready") and land on a live, mapless table. `RequireReady`
                sends them back to finish setup instead; everyone else falls
                through to `RequireSeat`'s own handling, unchanged. */}
            <RequireReady>
              {/* ⚠ **The same pattern as `RequireAuth`, one door further in.**
                  Nothing before 2026-08-31 asked "is there a seat here for you"
                  before opening the socket, which refuses with no seat exactly
                  as the REST layer does — so the table's own Owner, who the
                  campaign wizard explicitly allows to finish setup without
                  seating a character, got the same "campaign not found" a
                  stranger would. `CampaignPage` itself is untouched; this is a
                  door in front of it, not a change to what is behind it. */}
              <RequireSeat>
                <CampaignPage />
              </RequireSeat>
            </RequireReady>
          </RequireAuth>
        ),
      },
      {
        path: 'characters/new',
        element: (
          <RequireAuth>
            <NewCharacterPage />
          </RequireAuth>
        ),
      },
      {
        path: 'characters/:characterId',
        element: (
          <RequireAuth>
            <CharacterSheetPage />
          </RequireAuth>
        ),
      },
      {
        // Authenticated: the corpus is Open Game Content the app distributes,
        // but the GM-side slice is not something to hand out anonymously.
        path: 'rules',
        element: (
          <RequireAuth>
            <RulesPage />
          </RequireAuth>
        ),
      },
      {
        // Behind a login like the Rules Reference: it describes this
        // application's own surfaces, and there is nobody to read it who
        // cannot sign in.
        path: 'guide',
        element: (
          <RequireAuth>
            <GuidePage />
          </RequireAuth>
        ),
      },
      { path: 'login', element: <LoginPage /> },
      // Public by design, and it must stay that way: the OGL requires the
      // licence to ship with the content, and a copy behind a login is not
      // shipped with anything. Do not wrap this in RequireAuth.
      { path: 'licence', element: <LicencePage /> },
    ],
  },
]);
